import React, { useCallback, useLayoutEffect, useMemo, type ReactNode } from 'react';
// eslint-disable-next-line jira/graphql/restrict-graphql-in-unnamed-files
import { type PreloadedEntryPoint, graphql, useFragment } from 'react-relay';
import { toProjectType } from '@jira/platform__project-types/src/project-types.tsx';
import { isLoaderErrorAttributes } from '@jira/platform__react-loosely-lazy-error-handling/src/is-loader-error-attributes.tsx';
import {
	getEditionForProject,
	type ApplicationEdition,
} from '@jira/platform__application-editions/src/edition.tsx';
import OpTrigger from '@atlassian/jira-common-util-op-triggers/src/on-mount/OpTrigger.tsx';
import FetchError from '@atlassian/jira-fetch/src/utils/errors.tsx';
import { ContextualAnalyticsData, SCREEN } from '@atlassian/jira-product-analytics-bridge';
import UFOLabel from '@atlassian/jira-ufo-label/src/index.tsx';
import { useRouterActions } from '@atlassian/react-resource-router';
import { useAppEditions } from '@atlassian/jira-tenant-context-controller/src/components/app-editions/index.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import type { AnyEntryPoint } from '@atlassian/jira-entry-point/src/common/types.tsx';
import {
	ANALYTICS_DETAIL_VIEW_SOURCE_NAME,
	ANALYTICS_LIST_VIEW_SOURCE_NAME,
	PACKAGE_NAME,
	TEAM_NAME,
	isJiraSpreadsheetView,
} from '../../common/constants.tsx';
import type { OverridableIssueTableProps } from '../../common/types.tsx';
import { ListViewSkeleton } from '../../common/ui/loading-skeleton/index.tsx';
import { withReportErrors } from '../../common/ui/with-report-errors/index.tsx';
import { exclusiveGroupResultsOrIssueResultsFragment } from '../../common/utils/index.tsx';
import { markOnce, marks } from '../../common/utils/performance-analytics.tsx';
import { useSelectedIssueActions } from '../../controllers/selected-issue/hooks.tsx';
import { useSelectedViewState } from '../../controllers/selected-view-state/index.tsx';
import { SortContainer } from '../../controllers/sort-field-and-direction-state/index.tsx';
import { useResolvedJql } from '../../services/active-jql/index.tsx';
import { useIsFetching } from '../../services/issue-search/selectors.tsx';
import type { main_issueNavigator_ListView_filter$key as FilterFragment } from './list-view/__generated__/main_issueNavigator_ListView_filter.graphql';
import type {
	main_issueNavigator_IssueResults_project$key as ProjectFragment,
	JiraProjectType,
} from './__generated__/main_issueNavigator_IssueResults_project.graphql';
import type { main_issueNavigator_IssueResults_rankField$key as RankFieldFragment } from './__generated__/main_issueNavigator_IssueResults_rankField.graphql';
import type { main_issueNavigator_IssueResults_jiraView$key as JiraViewFragment } from './__generated__/main_issueNavigator_IssueResults_jiraView.graphql';
import type { main_issueNavigator_IssueResults_issueResults$key as IssueResultsFragment } from './__generated__/main_issueNavigator_IssueResults_issueResults.graphql';
import type { main_issueNavigator_IssueResults_groupResults$key as GroupResultsFragment } from './__generated__/main_issueNavigator_IssueResults_groupResults.graphql';
import DetailView from './detail-view/main.tsx';
import ErrorView, { RecoverableErrorViewBoundary } from './error-view/index.tsx';
import ListView from './list-view/main.tsx';

type Props = {
	hasServerError?: boolean;
	issueResults: IssueResultsFragment | null;
	groupResults: GroupResultsFragment | null;
	jiraView: JiraViewFragment | null;
	project: ProjectFragment | null;
	rankField: RankFieldFragment | null;
	onChangeColumnConfiguration?: () => void;
	onChangeJql?: (jql: string) => void;
	onCaughtIssueSearchError?: (error?: Error) => void;
	issueTableProps?: OverridableIssueTableProps;
	filter: FilterFragment | null;
	isIssueHierarchySupportEnabled?: boolean;
	isIssueRankingSupportEnabled?: boolean;
	isMergedColumnSupportEnabled?: boolean;
	isFrozenColumnSupportEnabled?: boolean;
	isInlineFieldConfigEnabled?: boolean;
	isInBetweenColumnPickerEnabled?: boolean;
	onIssueFlatListUpdated?: (
		newFlatKeyList: string[],
		newFlatIdList: string[],
		newFlatChildIdList: string[],
	) => void;
	issueAppEntryPoint?: PreloadedEntryPoint<AnyEntryPoint>;
};

const useAnalyticsAttributes = (type: JiraProjectType | null | undefined) => {
	const appEditions = useAppEditions();

	return useMemo(() => {
		if (!type) return;

		const projectType = toProjectType(type.toLowerCase());
		if (!projectType) return;

		const edition = getEditionForProject(projectType, appEditions);

		if (!edition) return;

		return { edition };
	}, [appEditions, type]);
};

const IssueResults = ({
	hasServerError = false,
	onChangeColumnConfiguration,
	onChangeJql,
	onCaughtIssueSearchError,
	onIssueFlatListUpdated,
	issueResults,
	groupResults,
	jiraView,
	project,
	rankField,
	issueTableProps,
	filter,
	isIssueHierarchySupportEnabled,
	isIssueRankingSupportEnabled,
	isMergedColumnSupportEnabled,
	isFrozenColumnSupportEnabled,
	isInlineFieldConfigEnabled,
	isInBetweenColumnPickerEnabled,
	issueAppEntryPoint,
}: Props) => {
	markOnce(marks.ISSUE_RESULTS_START);

	const [{ view: selectedView }] = useSelectedViewState();
	const jql = useResolvedJql();

	const { push } = useRouterActions();

	const { setSelectedIssueByIndex } = useSelectedIssueActions();

	const onSelectedRowChange = useCallback(
		(rowIndex: number) => {
			setSelectedIssueByIndex(rowIndex, {
				shouldDebounce: false,
				shouldNotifyOnChange: false,
				isSelectedByUserInteraction: true,
			});
		},
		[setSelectedIssueByIndex],
	);

	const isFetching = useIsFetching();

	const issueResultsData = useFragment<IssueResultsFragment>(
		// eslint-disable-next-line jira/graphql/restrict-graphql-in-unnamed-files
		graphql`
			fragment main_issueNavigator_IssueResults_issueResults on JiraIssueConnection
			@argumentDefinitions(
				projectKeys: { type: "[String!]" }
				projectKey: { type: "String" }
				shouldQueryHasChildren: { type: "Boolean!" }
				isConditionalFormattingEnabled: { type: "Boolean!" }
			) {
				...main_issueNavigator_ListView_issueResults
					@arguments(
						projectKeys: $projectKeys
						projectKey: $projectKey
						shouldQueryHasChildren: $shouldQueryHasChildren
						isConditionalFormattingEnabled: $isConditionalFormattingEnabled
					)
				...main_issueNavigator_DetailView_issueResults
			}
		`,
		issueResults,
	);

	const groupResultsData = useFragment<GroupResultsFragment>(
		// eslint-disable-next-line jira/graphql/restrict-graphql-in-unnamed-files
		graphql`
			fragment main_issueNavigator_IssueResults_groupResults on JiraSpreadsheetGroupConnection
			@argumentDefinitions(projectKeys: { type: "[String!]" }, projectKey: { type: "String" }) {
				...main_issueNavigator_ListView_groupResults
					@arguments(
						isPaginating: $isPaginating
						projectKeys: $projectKeys
						projectKey: $projectKey
					)
			}
		`,
		groupResults,
	);

	const jiraViewData = useFragment<JiraViewFragment>(
		// eslint-disable-next-line jira/graphql/restrict-graphql-in-unnamed-files
		graphql`
			fragment main_issueNavigator_IssueResults_jiraView on JiraView {
				__typename
				error {
					extensions {
						statusCode
					}
					message
				}
				... on JiraDetailedView {
					...main_issueNavigator_DetailView_view
				}
				... on JiraSpreadsheetView {
					...main_issueNavigator_ListView_view
				}
			}
		`,
		jiraView,
	);

	const projectData = useFragment(
		// eslint-disable-next-line jira/graphql/restrict-graphql-in-unnamed-files
		graphql`
			fragment main_issueNavigator_IssueResults_project on JiraProject
			@argumentDefinitions(
				isConditionalFormattingEnabled: { type: "Boolean!", defaultValue: false }
			) {
				...main_issueNavigator_ListView_project
					@arguments(isConditionalFormattingEnabled: $isConditionalFormattingEnabled)
				projectType
			}
		`,
		project,
	);

	const rankFieldData = useFragment<RankFieldFragment>(
		// eslint-disable-next-line jira/graphql/restrict-graphql-in-unnamed-files
		graphql`
			fragment main_issueNavigator_IssueResults_rankField on JiraQuery {
				...main_issueNavigator_ListView_rankField
			}
		`,
		rankField,
	);

	const attributes: { edition: ApplicationEdition } | undefined = useAnalyticsAttributes(
		projectData?.projectType,
	);

	const viewResultError = useMemo(() => {
		if (!jiraViewData) {
			return undefined;
		}
		const message = jiraViewData.error?.message ?? '';
		const statusCode = jiraViewData.error?.extensions?.[0].statusCode;

		if (typeof statusCode !== 'number') {
			return;
		}

		return new FetchError(statusCode, message);
	}, [jiraViewData]);

	useLayoutEffect(() => {
		markOnce(marks.ISSUE_RESULTS_END);
	}, []);

	const onNavigateToIssue = useCallback((issueKey: string) => push(`/browse/${issueKey}`), [push]);

	const sourceName =
		selectedView === 'detail' ? ANALYTICS_DETAIL_VIEW_SOURCE_NAME : ANALYTICS_LIST_VIEW_SOURCE_NAME;

	let viewContent: ReactNode = null;

	const resultsFragment = exclusiveGroupResultsOrIssueResultsFragment({
		groupResults: groupResultsData ?? null,
		issueResults: issueResultsData ?? null,
	});

	if (!hasServerError && jiraViewData && !jiraViewData.error) {
		if (
			selectedView === 'detail' &&
			jiraViewData.__typename === 'JiraDetailedView' &&
			resultsFragment?.issueResults
		) {
			viewContent = (
				<DetailView
					loading={isFetching}
					onNavigateToIssue={onNavigateToIssue}
					issueResults={resultsFragment.issueResults}
					view={jiraViewData}
					issueAppEntryPoint={
						fg('nin_issue_app_conditional_entrypoint') ? issueAppEntryPoint : undefined
					}
				/>
			);
		} else if (
			selectedView !== 'detail' &&
			isJiraSpreadsheetView(jiraViewData) &&
			resultsFragment
		) {
			viewContent = (
				<ListView
					viewResultError={viewResultError}
					loading={isFetching}
					onSelectedRowChange={onSelectedRowChange}
					onIssueFlatListUpdated={onIssueFlatListUpdated}
					view={jiraViewData}
					project={projectData ?? null}
					rankField={rankFieldData ?? null}
					onChangeColumnConfiguration={onChangeColumnConfiguration}
					issueTableProps={issueTableProps}
					isIssueHierarchySupportEnabled={isIssueHierarchySupportEnabled}
					isIssueRankingSupportEnabled={isIssueRankingSupportEnabled}
					isMergedColumnSupportEnabled={isMergedColumnSupportEnabled}
					isFrozenColumnSupportEnabled={isFrozenColumnSupportEnabled}
					isInlineFieldConfigEnabled={isInlineFieldConfigEnabled}
					isInBetweenColumnPickerEnabled={isInBetweenColumnPickerEnabled}
					filter={filter}
					{...resultsFragment}
				/>
			);
		}
	}

	if (!viewContent) {
		return isFetching ? <ListViewSkeleton /> : <ErrorView error={viewResultError} />;
	}

	return (
		<UFOLabel name="issue-results">
			<ContextualAnalyticsData
				sourceName={sourceName}
				sourceType={SCREEN}
				{...(attributes ? { attributes } : {})}
			>
				<RecoverableErrorViewBoundary
					recoveryProp={isFetching}
					onCaughtIssueSearchError={onCaughtIssueSearchError}
				>
					<SortContainer jql={jql} onChangeJql={onChangeJql}>
						{viewContent}
					</SortContainer>
					<OpTrigger
						op={() => {
							onCaughtIssueSearchError && onCaughtIssueSearchError(undefined);
						}}
					/>
				</RecoverableErrorViewBoundary>
			</ContextualAnalyticsData>
		</UFOLabel>
	);
};

export default withReportErrors<Props>(IssueResults, {
	id: 'ui.issue-results.unhandled',
	packageName: PACKAGE_NAME,
	teamName: TEAM_NAME,
	sendToPrivacyUnsafeSplunk: true,
	attributes: isLoaderErrorAttributes,
});
